import React, { useState, useEffect, } from 'react';
import { useParams } from 'react-router-dom';
import { fetchDashboardReportForUser } from '../../services/reportService';
import { Report } from '../../types/report';

import EnergyCapacityChart from './EnergyCapacityChart';
import useWeekRange from '../util/useWeekRange';

interface DashboardProps {
  user_id: string;
  // Add any props you need to pass to the component
}
const SolarUserSpecificDashboard: React.FC<DashboardProps> = () => {

  const { user_id } = useParams<{ user_id: string }>();
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const { start: weekStart, end: weekEnd, formattedLabel, handlePrevWeek, handleNextWeek, isNextDisabled } = useWeekRange();
  const [report, setReport] = useState<Partial<Report>>({});
  const [reportLoading, setReportLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!weekStart || !weekEnd) return;
    const fetchDashboardReportDataForUser = async () => {
      setReportLoading(true);
      await fetchDashboardReportForUser(user_id!, { start_date: weekStart, end_date: weekEnd })
        .then((response) => {
          setReport(response.data);
        }).catch((error) => {
          console.error('Error fetching profile:', error);
        }).finally(() => setReportLoading(false));
    };
    fetchDashboardReportDataForUser();
  }, [user_id, weekStart, weekEnd]);

  return (
    <>
      <div className="row">
        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-primary shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                    Today's Energy Generation {user_id}
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">{report.today_energy_generated} Kwh   </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-calendar fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-success shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                    Weekly Energy Generation</div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">{report.weekly_energy_generated} Kwh</div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-dollar-sign fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-info shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-info text-uppercase mb-1">Montly Energy Generation
                  </div>
                  <div className="row no-gutters align-items-center">
                    <div className="col-auto">
                      <div className="h5 mb-0 mr-3 font-weight-bold text-gray-800">{report.monthly_energy_generated} Kwh</div>
                    </div>
                    <div className="col">

                    </div>
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-clipboard-list fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-warning shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                    Yearly Energy Generation</div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">{report.yearly_energy_generated} Kwh</div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-comments fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-xl-6 col-lg-7">
          <div className="card shadow mb-4">
            <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between">
              <h6 className="m-0 font-weight-bold text-primary">Energy Generation Overview</h6>
              <div className="d-flex align-items-center gap-2">
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={handlePrevWeek}>Prev Week</button>
                <span className="mx-2 small text-muted">{formattedLabel}</span>
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleNextWeek} disabled={isNextDisabled}>Next Week</button>
              </div>
            </div>
            <div className="card-body">
              {reportLoading ? (
                <div className="d-flex align-items-center justify-content-center" style={{ height: 320, minWidth: 0 }}>
                  <div className="spinner-border text-primary me-2" role="status" aria-label="Loading chart" />
                  <span className="text-muted small">Loading...</span>
                </div>
              ) : (
                <EnergyCapacityChart weeklyByDay={report.weekly_energy_generated_by_day as any} />
              )}
            </div>
          </div>
        </div>

        <div className="col-xl-6 col-lg-7">
          <div className="card shadow mb-4 col-xl-12 ">
            <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between">
              <h6 className="m-0 font-weight-bold text-primary">Consumer Details</h6>
            </div>
            <div className="card-body">
              <div className="chart-bar">
                <p>Consumer Name: {report.user?.consumer_name}</p>  
                <p>Consumer Type: {report.user?.consumer_type}</p>
                <p>Discom Number: {report.user?.discom_number}</p>
                <p>Provider Name: {report.provider?.provider_name}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SolarUserSpecificDashboard;